import { useRef, useEffect } from 'react';

import { readCache, writeCache } from '~helpers/sessionCache';
import { useListing } from './useListing';

// Wraps useListing with TTL-based sessionStorage persistence so a result list restores
// instantly when the user navigates back to it (e.g. from a profile page). The snapshot
// seeds useListing's initial state synchronously on first render - no skeleton - while the
// listing's own effects refresh in the background.
//
// cacheKey must identify the search AND be available on first render; key on the URL
// (path + query), not on async location state, or the synchronous seed is missed.

const FIVE_MINUTES = 5 * 60 * 1000;

export const buildCacheKey = (parts) => `listing:${parts.filter((p) => p != null && p !== '').join('|')}`;

const readSnapshot = (key, ttl) => readCache(key, ttl) || { results: [], count: null };

export const useListingState = ({ cacheKeyParts = [], ttl = FIVE_MINUTES, ...listingOptions }) => {
  const cacheKey = buildCacheKey(cacheKeyParts);

  // Read the snapshot once, synchronously, to seed initial state before the first paint.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    initialRef.current = readSnapshot(cacheKey, ttl);
  }

  // Set when a non-empty snapshot is restored for the current key. consumeFreshSnapshot()
  // lets a consumer skip its initial fetch once; any key change or refetch clears it, so
  // changing filters/page/sort still loads fresh data.
  const freshSnapshotRef = useRef(initialRef.current.results.length > 0);

  const listing = useListing({
    ...listingOptions,
    initialResults: initialRef.current.results,
    initialCount: initialRef.current.count,
  });

  const { results, count, setResults, setCount } = listing;

  const consumeFreshSnapshot = () => {
    const fresh = freshSnapshotRef.current;
    freshSnapshotRef.current = false;
    return fresh;
  };

  // The exact array a snapshot restored - persisting it again would renew its own TTL.
  const restoredRef = useRef(initialRef.current.results);

  // On key change (paging/sorting/another list in the same mount) the held results belong
  // to the previous search: restore the new key's snapshot if present, else clear so the
  // new search starts clean instead of showing the stale list while it loads.
  const seededKeyRef = useRef(cacheKey);
  useEffect(() => {
    if (seededKeyRef.current === cacheKey) return;
    seededKeyRef.current = cacheKey;

    const { results: cached, count: cachedCount } = readSnapshot(cacheKey, ttl);
    restoredRef.current = cached;
    setResults(cached);
    if (cached.length > 0) setCount(cachedCount);
    freshSnapshotRef.current = cached.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Persist for return navigation. Never on a key change alone - that would file the
  // old page's results under the new page's key.
  const prevResultsRef = useRef(initialRef.current.results);
  const lastWriteRef = useRef({ key: null, results: null });
  useEffect(() => {
    const isNewData = results !== prevResultsRef.current;
    prevResultsRef.current = results;

    if (!results || results.length === 0 || results === restoredRef.current) {
      return;
    }

    const isCountUpdate = lastWriteRef.current.key === cacheKey && lastWriteRef.current.results === results;
    if (isNewData || isCountUpdate) {
      writeCache(cacheKey, { results, count });
      lastWriteRef.current = { key: cacheKey, results };
    }
  }, [cacheKey, results, count]);

  return { ...listing, consumeFreshSnapshot };
};
